/**
 * WorkoutAccessAudit — owner-only "who can see workouts?" panel.
 *
 * Hits /api/coach/workout-access-audit and groups the roster by access
 * reason: active sub, comp_access, dunning-suspended, or no sub at all.
 * When a member reports "I can't see workouts" it's a one-glance lookup.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, Search, CheckCircle2, XCircle, AlertTriangle, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_TONES = {
    active_subscription: { tone: "text-emerald-300 border-emerald-700/40 bg-emerald-950/20", Icon: CheckCircle2 },
    comp_access: { tone: "text-sky-300 border-sky-700/40 bg-sky-950/20", Icon: Sparkles },
    dunning_suspended: { tone: "text-amber-300 border-amber-700/40 bg-amber-950/20", Icon: AlertTriangle },
    no_sub: { tone: "text-zinc-400 border-zinc-800 bg-zinc-950/40", Icon: XCircle },
};

export default function WorkoutAccessAudit() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");

    useEffect(() => {
        axios.get(`${API}/coach/workout-access-audit`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch(() => setData({ rows: [], counts: {}, total: 0 }));
    }, []);

    if (!data) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    const rows = data.rows.filter((r) => {
        if (filter === "has_access" && !r.access) return false;
        if (filter === "locked" && r.access) return false;
        if (filter === "suspended" && r.reason !== "dunning_suspended") return false;
        if (q.trim()) {
            const needle = q.trim().toLowerCase();
            return r.name.toLowerCase().includes(needle) || r.email.toLowerCase().includes(needle);
        }
        return true;
    });

    return (
        <div data-testid="workout-access-audit" className="min-h-screen bg-brand-obsidian text-white pt-24 pb-12 px-4 md:px-10">
            <div className="max-w-5xl mx-auto">
                <Link to="/coach" className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-brand-red mb-3">
                    <ArrowLeft className="w-3 h-3" /> Coach console
                </Link>
                <h1 className="font-display text-3xl md:text-4xl tracking-wider mb-1">Programs Access Audit</h1>
                <p className="text-sm text-zinc-400 mb-6">
                    Everyone with workout/program access today, with the reason. {data.total} total clients on file.
                </p>

                {/* Summary tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Tile label="Has access" count={data.counts.has_access || 0} tone="emerald" onClick={() => setFilter("has_access")} active={filter === "has_access"} />
                    <Tile label="Locked" count={data.counts.locked || 0} tone="zinc" onClick={() => setFilter("locked")} active={filter === "locked"} />
                    <Tile label="Suspended (dunning)" count={data.counts.suspended || 0} tone="amber" onClick={() => setFilter("suspended")} active={filter === "suspended"} />
                    <Tile label="Show all" count={data.rows.length} tone="zinc" onClick={() => setFilter("all")} active={filter === "all"} />
                </div>

                {/* Search */}
                <div className="flex items-center gap-2 mb-4 border border-zinc-800 bg-zinc-950 px-3 py-2">
                    <Search className="w-3.5 h-3.5 text-zinc-500" />
                    <input
                        data-testid="audit-search"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name or email"
                        className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-500 focus:outline-none"
                    />
                </div>

                {rows.length === 0 ? (
                    <div className="border border-zinc-800 p-8 text-center text-sm text-zinc-400">No matches.</div>
                ) : (
                    <div className="border border-zinc-800 divide-y divide-zinc-900">
                        {rows.map((r) => {
                            const tone = REASON_TONES[r.reason] || REASON_TONES.no_sub;
                            const Icon = tone.Icon;
                            return (
                                <Link
                                    key={r.user_id}
                                    to={`/coach/leads/${r.user_id}`}
                                    data-testid={`audit-row-${r.user_id}`}
                                    className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-zinc-900/40 transition-colors"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-white truncate">{r.name || "—"}</div>
                                        <div className="text-[11px] tracking-[0.1em] text-zinc-500 truncate">{r.email}</div>
                                    </div>
                                    <span className={`shrink-0 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${tone.tone}`}>
                                        <Icon className="w-3 h-3" />
                                        {r.reason_label}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function Tile({ label, count, tone, onClick, active }) {
    const tones = {
        emerald: active ? "border-emerald-500 bg-emerald-950/30 text-emerald-300" : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-emerald-700",
        zinc: active ? "border-brand-red bg-brand-red/10 text-white" : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-600",
        amber: active ? "border-amber-500 bg-amber-950/30 text-amber-300" : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-amber-700",
    };
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`audit-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`text-left border px-4 py-3 transition-colors ${tones[tone] || tones.zinc}`}
        >
            <div className="font-display text-3xl tracking-wider">{count}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase mt-0.5">{label}</div>
        </button>
    );
}
